/**
 * BottomNav.jsx
 *
 * Fixed bottom navigation bar used on all authenticated pages (mobile-first).
 *
 * Renders four quick-access buttons:
 *   Camera  – triggers a hidden file input (id passed via `cameraInputId` prop)
 *   Rank    – navigates to /photo-upload-interface
 *   Results – navigates to /ranking-results-display
 *   Menu    – opens a slide-up overlay with full navigation + user info
 *
 * The slide-up menu overlay includes:
 *   - User avatar, name, email, and credit balance
 *   - All main navigation links
 *   - Purchase Credits CTA
 *   - Sign Out button
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

/** Navigation items shown in the slide-up menu overlay */
const NAV_ITEMS = [
  { label: 'Home',         path: '/user-dashboard',          icon: 'Home',     description: 'Go to your dashboard' },
  { label: 'Upload Photos',path: '/photo-upload-interface',  icon: 'Upload',   description: 'Upload and analyze your photos' },
  { label: 'View Results', path: '/ranking-results-display', icon: 'BarChart3',description: 'See your photo rankings' },
  { label: 'History',      path: '/dashboard-analysis-history', icon: 'History', description: 'View past analyses' },
];

/**
 * @param {object}  props
 * @param {string}  props.activePath     - Current page path (highlights the active tab)
 * @param {string}  [props.cameraInputId] - ID of a hidden <input type="file"> to trigger on Camera tap
 */
const BottomNav = ({ activePath, cameraInputId }) => {
  const navigate = useNavigate();
  const { userProfile, user, creditBalance, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Derive display values from auth context
  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';

  // ─── Handlers ──────────────────────────────────────────────────────────────

  /** Programmatically clicks the hidden camera file input */
  const handleCameraClick = () => {
    if (cameraInputId) document.getElementById(cameraInputId)?.click();
  };

  const toggleMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const isActive = (path) => activePath === path;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Bottom tab bar ─────────────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-around z-40">
        <button
          onClick={handleCameraClick}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Camera" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Camera</span>
        </button>

        <button
          onClick={() => navigate('/photo-upload-interface')}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Image" size={28} color={isActive('/photo-upload-interface') ? '#9333EA' : '#9CA3AF'} />
          <span className={`text-sm ${isActive('/photo-upload-interface') ? 'font-semibold text-purple-600' : 'text-gray-400'}`}>
            Rank
          </span>
        </button>

        <button
          onClick={() => navigate('/ranking-results-display')}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="BarChart2" size={28} color={isActive('/ranking-results-display') ? '#9333EA' : '#9CA3AF'} />
          <span className={`text-sm ${isActive('/ranking-results-display') ? 'font-semibold text-purple-600' : 'text-gray-400'}`}>
            Results
          </span>
        </button>

        <button
          onClick={toggleMenu}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Menu" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Menu</span>
        </button>
      </div>
      {/* ── Slide-up menu overlay ───────────────────────────────────────── */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={toggleMenu}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e?.stopPropagation()} // prevent backdrop click from closing when tapping inside
          >
            {/* Header row */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Menu</h3>
              <button onClick={toggleMenu} className="p-2">
                <Icon name="X" size={24} color="#6B7280" />
              </button>
            </div>

            {/* User info card */}
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                {/* Avatar initial */}
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {displayName?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{displayName}</div>
                  <div className="text-sm text-gray-600">{displayEmail}</div>
                </div>
              </div>

              {/* Credit balance */}
              <div className="mt-3 pt-3 border-t border-purple-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Credits Remaining</span>
                  <span className="text-lg font-bold text-purple-600">{creditBalance ?? 0}</span>
                </div>
              </div>
            </div>

            {/* Navigation links */}
            <div className="space-y-2 mb-6">
              {NAV_ITEMS?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => { navigate(item?.path); toggleMenu(); }}
                  className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Icon name={item?.icon} size={24} color="#9333EA" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{item?.label}</div>
                    <div className="text-xs text-gray-500">{item?.description}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              <button
                onClick={() => { navigate('/purchase-credits'); toggleMenu(); }}
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
              >
                Purchase Credits
              </button>
              <button
                onClick={() => { handleLogout(); toggleMenu(); }}
                className="w-full flex items-center justify-center gap-2 text-gray-600 font-medium py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="LogOut" size={20} color="#6B7280" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;
